// These two imports will be stripped out by Webpack if no Sentry configuration has been specifiec
import * as Sentry from "@sentry/react";
import {
    browserTracingIntegration,
    reactRouterV6BrowserTracingIntegration,
} from "@sentry/react";
import { extraErrorDataIntegration } from "@sentry/integrations";

import { useEffect } from "react";
import {
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes,
    Routes,
} from "react-router-dom";
import { GameParams } from "../validation/game-params";

const sentryExists = !!window.SENTRY_DSN;

export const initialiseSentry = sentryExists
    ? (opts: Sentry.BrowserOptions, buildInfo: BuildInfo): void => {
          Sentry.init({
              maxValueLength: 3000,
              environment: buildInfo.environmentName,
              release: buildInfo.buildId,
              integrations: [
                  browserTracingIntegration(),
                  reactRouterV6BrowserTracingIntegration({
                      useEffect,
                      useLocation,
                      useNavigationType,
                      createRoutesFromChildren,
                      matchRoutes,
                  }),
                  extraErrorDataIntegration(),
              ],
              tracesSampleRate: 0.01,
              sampleRate: 0.01,
              ...opts,
          });
      }
    : (_: Sentry.BrowserOptions, __: BuildInfo): void => {
          return;
      };

export const captureManualException = sentryExists
    ? (ex: Error): void => {
          Sentry.captureException(ex);
      }
    : (_: Error): void => {
          return;
      };

export const captureMessage = sentryExists
    ? (msg: string, severity: Sentry.SeverityLevel = "debug"): void => {
          Sentry.captureMessage(msg, severity);
      }
    : (_: string, __: Sentry.SeverityLevel = "debug"): void => {
          return;
      };

export const withSentryTracing = sentryExists
    ? (component: (props: unknown) => JSX.Element) =>
          Sentry.withProfiler(component)
    : (component: (props: unknown) => JSX.Element) => component;

export const withSentryRoutes = sentryExists
    ? () => Sentry.withSentryReactRouterV6Routing(Routes)
    : () => Routes;

export const setUser = sentryExists
    ? (id: number | string) => {
          try {
              Sentry.setUser({ id: String(id) });
          } catch {
              return;
          }
      }
    : () => {
          return;
      };

export const setGameParams = sentryExists
    ? (gameParams: GameParams) => {
          try {
              const { channel, country, platform, maintenance } = gameParams;
              Sentry.setContext("Game Parameters", {
                  channel,
                  country,
                  platform,
                  maintenance,
              });
          } catch {
              return;
          }
      }
    : () => {
          return;
      };
